package com.example.readingnotes.presentation.postdetail

import com.example.readingnotes.domain.usecase.CreateCommentUseCase
import com.example.readingnotes.domain.usecase.LoadCommentsUseCase
import com.example.readingnotes.domain.usecase.LoadPostDetailUseCase
import com.example.readingnotes.platform.SocialShare
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class PostDetailViewModel(
    private val loadPostDetailUseCase: LoadPostDetailUseCase,
    private val loadCommentsUseCase: LoadCommentsUseCase,
    private val createCommentUseCase: CreateCommentUseCase,
    private val viewModelScope: CoroutineScope,
) {
    private val _state = MutableStateFlow(PostDetailState())
    val state: StateFlow<PostDetailState> = _state.asStateFlow()

    private var currentPostId: Int? = null

    fun onEvent(event: PostDetailEvent) {
        when (event) {
            is PostDetailEvent.OnAppear -> loadData(event.postId)
            is PostDetailEvent.OnAuthorNameChanged -> {
                _state.update { it.copy(authorNameInput = event.text) }
            }
            is PostDetailEvent.OnCommentContentChanged -> {
                _state.update { it.copy(commentInput = event.text, commentErrorMessage = null) }
            }
            is PostDetailEvent.OnCommentSubmit -> submitComment()
            is PostDetailEvent.OnShareClick -> handleShareClick()
            is PostDetailEvent.OnCopyClick -> handleCopyClick()
            is PostDetailEvent.OnLoadComments -> loadCommentsOnly(event.postId)
        }
    }

    private fun loadData(postId: Int) {
        currentPostId = postId
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            val postDeferred = async { loadPostDetailUseCase(postId) }
            val commentsDeferred = async { loadCommentsUseCase(postId) }

            val postResult = postDeferred.await()
            val commentsResult = commentsDeferred.await()

            postResult
                .onSuccess { post ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            post = post,
                            comments = commentsResult.getOrDefault(emptyList()),
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    // SSE: Reload comments only (for real-time updates)
    private fun loadCommentsOnly(postId: Int) {
        viewModelScope.launch {
            loadCommentsUseCase(postId)
                .onSuccess { comments ->
                    _state.update { it.copy(comments = comments) }
                }.onFailure { error ->
                    println("Failed to reload comments: ${error.message}")
                }
        }
    }

    private fun submitComment() {
        val postId = currentPostId ?: return
        val content = _state.value.commentInput

        if (content.isBlank()) {
            _state.update { it.copy(commentErrorMessage = "コメントを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isSubmittingComment = true, commentErrorMessage = null) }

            createCommentUseCase(
                postId = postId,
                authorName = _state.value.authorNameInput.takeIf { it.isNotBlank() },
                content = content,
            ).onSuccess {
                _state.update { it.copy(commentInput = "", isSubmittingComment = false) }
                // Reload comments
                loadCommentsUseCase(postId)
                    .onSuccess { comments ->
                        _state.update { it.copy(comments = comments) }
                    }
            }.onFailure { error ->
                _state.update {
                    it.copy(
                        isSubmittingComment = false,
                        commentErrorMessage = ErrorMessageProvider.getLocalizedMessage(error, "コメント送信に失敗しました"),
                    )
                }
            }
        }
    }

    private fun handleShareClick() {
        val post = _state.value.post ?: return
        val postUrl = "https://reading-notes.app/post/${post.id}"

        viewModelScope.launch {
            try {
                val success =
                    SocialShare.shareContent(
                        title = post.title,
                        text = post.contentMarkdown,
                        url = postUrl,
                    )
                if (success) {
                    println("Share successful for post: ${post.title}")
                } else {
                    println("Share failed or was cancelled for post: ${post.title}")
                }
            } catch (e: Exception) {
                println("Error sharing post: ${e.message}")
            }
        }
    }

    private fun handleCopyClick() {
        val post = _state.value.post ?: return
        val postUrl = "https://reading-notes.app/post/${post.id}"
        val copyText = "${post.title}\n${post.contentMarkdown}\n$postUrl"

        viewModelScope.launch {
            try {
                val success = SocialShare.copyToClipboard(copyText)
                if (success) {
                    println("Copy to clipboard successful for post: ${post.title}")
                } else {
                    println("Copy to clipboard failed for post: ${post.title}")
                }
            } catch (e: Exception) {
                println("Error copying to clipboard: ${e.message}")
            }
        }
    }
}
